// Assemble the captured conversations a boundary POST publishes.
//
// An "exchange" is one LLM call: what the model saw, plus what pi parsed out of
// the response (usage, model, stop reason). pi already did that parsing to
// render the transcript, so capture is a re-serialization of
// `agent.state.messages`, not a second parse of the wire.
//
// Each assistant message in the history IS one completed call, and the messages
// before it are that call's request. That reconstruction has one honest
// imprecision: compaction rewrites the history, so an OLD call's prefix is
// whatever survives now rather than what it literally sent. Only the newest
// exchanges are ever pushed (see the cursor below), so in practice the prefix
// is the real request.
//
// Identity is the assistant message's `timestamp`. It is stable across
// re-serialization (unlike an array index, which compaction shifts) and
// monotonic within a conversation, so it doubles as the "already pushed"
// cursor and as the seed for the server's deterministic event id.
//
// WHAT GOES ON THE WIRE, and why it is shaped this way. Every call in a
// conversation sent a prefix of the same message list, so the conversation's
// transcript travels ONCE and each exchange carries only how many of those
// messages it sent. Giving each call its own copy of its prefix instead costs
// O(n^2) in the call count -- measured ~4.5x the conversation at 10 calls and
// ~30x by 60 -- which the upload budget below then rations into publishing only
// part of a long run. The transcript is a `messages_json` STRING for the same
// reason `payload_json` is one: the server re-serializes it and never queries
// into it.

import { wireByteLen } from "./chatSyncSerialize.js";

// Budget for ONE conversation's transcript, in WIRE bytes (see the measurement
// helpers below). Over it, the OLDEST messages are dropped -- never the newest,
// which is what the calls were about -- and every exchange in the conversation
// is marked truncated, because they all lost that context.
//
// Sized to clear a full model context: ~0.8 MiB of wire bytes for 200k tokens of
// plain text, but ~1.1 MiB once tool results are JSON tables (this product's
// normal analyst workload -- JSON inside a JSON string double-escapes, so every
// quote costs 4 bytes by the time postChatSync stringifies the body).
export const MAX_CONVERSATION_BYTES = 3 * 1024 * 1024;

// Whole-POST budget across every conversation. The gateway caps the
// DECOMPRESSED SyncChat body at 8 MiB and turns share that ceiling --
// chatSyncSerialize's MAX_SYNC_BATCH_BYTES already claims 7 MiB. Capture rides
// the first POST, so the engine reserves whatever it measures out of that first
// batch's turn budget (chunkTurnsBySize's firstBatchReserveBytes); this cap is
// what bounds the reservation, leaving >= 4 MiB of the first batch for turns.
//
// A conversation that does not fit is held back WHOLE for the next boundary
// rather than split -- its cursor does not advance, so nothing is lost, and the
// `deferred` count it returns is metered server-side so a chat publishing only
// part of each run stays visible.
export const MAX_CAPTURE_BYTES = 3 * 1024 * 1024;

// Hard cap on exchanges in one POST, mirroring the server's own bound.
export const MAX_EXCHANGES_PER_SYNC = 256;

// The chat's own agent loop. Any other conversation key is a sub-agent.
export const PARENT_CONVERSATION = "primary";

// Conversation key for one sub-agent. `toolCallId` is the spawning tool_use id
// (also the child-handle registry's key); a batch item appends its index. The
// server hashes this into the child's own trace id, so it must be stable for
// the life of the chat — which the tool-call id is.
export function childConversation(toolCallId, itemIndex = null) {
    const suffix = itemIndex == null ? "" : `#${itemIndex}`;
    return `child:${toolCallId}${suffix}`;
}

// Walk a chat's rendered items for sub-agent transcripts. A completed spawn
// leaves the child's messages on the tool node's `details` (`details.messages`
// for spawn_subagent, `details.items[].messages` for spawn_batch); a child still
// running in the background is mirrored there too by the child-handle registry.
export function collectSubAgentConversations(items) {
    const out = [];
    const walk = (nodes) => {
        if (!Array.isArray(nodes)) return;
        for (const node of nodes) {
            if (!node || typeof node !== "object") continue;
            const details = node.details;
            if (node.kind === "tool" && details && typeof details === "object") {
                if (Array.isArray(details.messages) && details.messages.length) {
                    out.push({
                        conversation: childConversation(node.id),
                        parentToolCallId: String(node.id),
                        messages: details.messages,
                        systemPrompt: details.systemPrompt || null,
                    });
                }
                if (Array.isArray(details.items)) {
                    details.items.forEach((item, index) => {
                        if (!Array.isArray(item?.messages) || !item.messages.length) return;
                        out.push({
                            conversation: childConversation(node.id, index),
                            parentToolCallId: String(node.id),
                            messages: item.messages,
                            systemPrompt: item.systemPrompt || details.systemPrompt || null,
                        });
                    });
                }
            }
            walk(node.children);
        }
    };
    walk(items);
    return out;
}

// One pi message as the typed wire message (`CapturedMessage`).
//
// Two jobs. It strips pi's bookkeeping - assistant `usage` above all, which is
// hoisted onto the exchange and would otherwise be re-sent inside every later
// request - and it names the message TYPE on the contract rather than leaving
// it to be inferred downstream.
//
// That second job is why this is a typed shape at all. A notification is
// context the dashboard injected, not something the user typed, and the
// provider body the server synthesizes has no role for it; encoding the
// difference as user text plus a marker string meant two languages had to agree
// on a spelling to recover it, and a user who typed the marker was
// misclassified. Here it is simply a different variant.
function toWireMessage(message) {
    const variant = toWireVariant(message);
    if (!variant) return null;
    // When the agent loop recorded the message. The server makes it the
    // message's own time, so the prompt is not dated by the reply after it.
    const at = Number(message.timestamp);
    return Number.isFinite(at) && at > 0 ? { ...variant, at_ms: at } : variant;
}

function toWireVariant(message) {
    if (!message || typeof message !== "object") return null;
    switch (message.role) {
        case "user":
            return { user: { blocks: toWireBlocks(message.content) } };
        case "assistant":
            return { assistant: { blocks: toWireBlocks(message.content) } };
        case "toolResult": {
            const toolResult = {
                tool_use_id: message.toolCallId ?? "",
                is_error: !!message.isError,
                content: toWireBlocks(message.content),
            };
            if (message.toolName) toolResult.tool_name = String(message.toolName);
            return { tool_result: toolResult };
        }
        case "notification":
            return { notification: { text: message.text ?? "" } };
        default:
            // A role neither side knows: skip rather than ship an
            // unclassifiable body. Nothing here is load-bearing for the
            // conversation's shape.
            return null;
    }
}

// One message's content as typed blocks.
//
// pi allows a bare string body on user messages; the contract does not, so that
// shorthand is normalized to a single text block here rather than leaving the
// server to accept two shapes for one thing.
//
// `arguments_json` stays a JSON STRING: tool arguments are whatever a tool
// declares, and the server copies them into the published body without parsing.
// Typing them would mean re-encoding a value nobody inspects.
function toWireBlocks(content) {
    if (typeof content === "string") return content ? [{ text: content }] : [];
    if (!Array.isArray(content)) return [];
    const blocks = [];
    for (const block of content) {
        if (!block || typeof block !== "object") continue;
        switch (block.type) {
            case "text":
                blocks.push({ text: block.text ?? "" });
                break;
            case "thinking":
                blocks.push({ thinking: block.thinking ?? "" });
                break;
            case "image":
                blocks.push({
                    image: { mime_type: block.mimeType ?? "", data: block.data ?? "" },
                });
                break;
            case "toolCall":
                blocks.push({
                    tool_use: {
                        id: block.id ?? "",
                        name: block.name ?? "",
                        arguments_json: JSON.stringify(block.arguments ?? {}),
                    },
                });
                break;
            default:
                // A block kind this build doesn't know. Skipping it matches the
                // server, which reads an unset block oneof the same way.
                break;
        }
    }
    return blocks;
}

// The bytes a message list contributes once it is on the wire.
//
// One stringify, not two. The messages used to travel as a `messages_json`
// STRING inside a body that postChatSync then stringified, so every `"` and `\`
// doubled and the budget had to account for that escaping. Typed messages are
// part of the body's own structure, so they are serialized exactly once and the
// old double-encode would now over-count a text-heavy conversation by 15-30% --
// which would defer capture that comfortably fits.
//
// UTF-8 bytes, not `.length`: the server's cap is byte-accurate and sits only
// 1 MiB above this client budget, which a CJK transcript's ~3x undercount
// clears easily -- and an over-cap SyncChat is rejected whole, turns and head
// included, on every retry.
function requestWireBytes(messages) {
    return wireByteLen(JSON.stringify(messages));
}

// One message's contribution to the number above: the length of its own JSON,
// without the surrounding brackets. Byte length distributes over concatenation
// exactly as character length does, so the decomposition below stays exact.
function messageWireSize(wireMessage) {
    return wireByteLen(JSON.stringify(wireMessage));
}

// Everything in requestWireBytes that is not a message: "[" and "]".
const WIRE_ENVELOPE_BYTES = 2;

// requestWireBytes, recomputed from per-message sizes instead of re-serializing
// the list.
//
// Exact, not an estimate: `JSON.stringify` of an array is the concatenation
// `[ m0 , m1 , ... ]`, so each message contributes its own serialized length and
// each gap one comma. Pinned against requestWireBytes by test.
//
// This is what makes the trim loop below linear. Measuring by re-serializing
// meant every dropped message re-encoded the entire remaining history, and
// every exchange re-encoded the whole prefix it shares with its siblings --
// quadratic in the length of a run, on the main thread, at run end.
function wireBytesOf(sizes, from = 0) {
    let total = WIRE_ENVELOPE_BYTES + Math.max(0, sizes.length - from - 1);
    for (let i = from; i < sizes.length; i++) total += sizes[i];
    return total;
}

// Marker left in place of clipped text, so a reader of the published body can
// tell a short message from a shortened one.
const CLIP_NOTICE = "…[clipped for upload]";

// Headroom the clip leaves for JSON structure and the notices themselves. The
// per-block division bounds text, not the envelope around it. With a
// pathological block COUNT the 256-byte floor can still overrun the
// per-exchange budget — the whole-POST budget (well under the route's cap) is
// the backstop that keeps that from reaching the wire.
const CLIP_ENVELOPE_RESERVE_BYTES = 4096;

// Cut `text` to at most `maxBytes` UTF-8 bytes without splitting a code point.
// Clipping by `.slice(maxBytes)` would cut CHARACTERS against a BYTE budget and
// leave a CJK block ~3x over it -- this stage is the backstop for a body the
// route would otherwise reject outright, so it has to land under the budget in
// the units the budget is written in.
const _clipEncoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;
const _clipDecoder = typeof TextDecoder !== "undefined" ? new TextDecoder() : null;

function sliceToBytes(text, maxBytes) {
    if (!_clipEncoder || !_clipDecoder) return text.slice(0, maxBytes);
    const bytes = _clipEncoder.encode(text);
    if (bytes.length <= maxBytes) return text;
    // Back off any UTF-8 continuation byte (10xxxxxx) so the cut lands on a code
    // point boundary; a split sequence decodes to U+FFFD, which is longer than
    // what it replaced. Encoding once and cutting keeps this linear -- measuring
    // candidate prefixes in a loop is quadratic on a megabyte tool result.
    let end = maxBytes;
    while (end > 0 && (bytes[end] & 0xc0) === 0x80) end--;
    return _clipDecoder.decode(bytes.subarray(0, end));
}

// Clip one block's text-bearing fields to `perBlockBytes`. Images are dropped
// to a placeholder rather than clipped — half a base64 payload is not a
// smaller image, it's a corrupt one.
function clipBlock(block, perBlockBytes) {
    if (!block || typeof block !== "object") return block;
    if (block.image) {
        return { text: `[image omitted: ${block.image.mime_type || "unknown"}]` };
    }
    const out = { ...block };
    for (const key of ["text", "thinking"]) {
        if (typeof out[key] === "string" && wireByteLen(out[key]) > perBlockBytes) {
            out[key] = sliceToBytes(out[key], perBlockBytes) + CLIP_NOTICE;
        }
    }
    return out;
}

// The blocks of a typed wire message, whichever variant it is.
//
// The variant wrapper is what the clipper has to see past: a message is
// `{user:{blocks}}` / `{assistant:{blocks}}` / `{tool_result:{content}}`, and a
// notification carries text directly with no blocks at all.
function messageBlocks(message) {
    if (message?.user) return message.user.blocks;
    if (message?.assistant) return message.assistant.blocks;
    if (message?.tool_result) return message.tool_result.content;
    return null;
}

function clipMessage(message, perBlockBytes) {
    const blocks = messageBlocks(message);
    if (!Array.isArray(blocks)) {
        // A notification's whole payload is one string, so it is clipped in
        // place rather than through the block list.
        if (message?.notification && wireByteLen(message.notification.text) > perBlockBytes) {
            return {
                notification: {
                    text: sliceToBytes(message.notification.text, perBlockBytes) + CLIP_NOTICE,
                },
            };
        }
        return message;
    }
    const clipped = blocks.map((b) => clipBlock(b, perBlockBytes));
    if (message.user) return { user: { blocks: clipped } };
    if (message.assistant) return { assistant: { blocks: clipped } };
    return { tool_result: { ...message.tool_result, content: clipped } };
}

// Fit a conversation's transcript inside `maxBytes` of WIRE bytes, in two
// stages.
//
// First drop the OLDEST messages — the cheapest loss, and the part the model
// itself would have compacted away next. `sizes[i]` is the precomputed wire
// size of `messages[i]`, so dropping one is a subtraction (its own bytes plus
// the comma that separated it) rather than a re-measure; that is what keeps a
// long run's boundary off an O(n^2) re-serialize.
//
// `maxDrop` is the floor: dropping past the shortest request would leave that
// call describing a conversation it never saw, so trimming stops there and the
// second stage takes over.
//
// A single message can still exceed the budget on its own (a tool result can
// legitimately run to megabytes, up to the model's context ceiling), and the
// route rejects an over-cap body outright — which would park the whole chat in
// the outbox on a 400/413 with no hot retry. So the second stage clips the
// surviving blocks' text. It runs at most once, so it re-measures directly.
function fitConversation(messages, sizes, maxDrop, maxBytes) {
    let dropped = 0;
    let total = wireBytesOf(sizes);
    let truncated = false;
    while (dropped < maxDrop && total > maxBytes) {
        total -= sizes[dropped] + 1;
        dropped++;
        truncated = true;
    }
    let kept = messages.slice(dropped);
    if (requestWireBytes(kept) > maxBytes) {
        const blockCount = kept.reduce(
            (n, m) => n + (messageBlocks(m)?.length ?? 1),
            0,
        );
        // Reserve room for the JSON envelope and the clip notices themselves,
        // which sit outside the per-block text the budget divides up.
        const budget = Math.max(1024, maxBytes - CLIP_ENVELOPE_RESERVE_BYTES);
        const perBlockBytes = Math.max(256, Math.floor(budget / Math.max(blockCount, 1)));
        kept = kept.map((m) => clipMessage(m, perBlockBytes));
        truncated = true;
    }
    return { messages: kept, dropped, truncated };
}

// Copy one reported token count onto the exchange under its proto field name.
// Only counts the provider actually reported are forwarded: an absent field
// means "not observed", which must stay distinct from a reported zero all the
// way into the token columns.
const USAGE_FIELDS = [
    ["input", "input_tokens"],
    ["output", "output_tokens"],
    ["cacheRead", "cache_read_tokens"],
    ["cacheWrite", "cache_write_tokens"],
    ["reasoning", "reasoning_tokens"],
];

function applyUsage(exchange, usage) {
    if (!usage || typeof usage !== "object") return;
    for (const [piKey, wireKey] of USAGE_FIELDS) {
        if (Number.isFinite(usage[piKey])) exchange[wireKey] = usage[piKey];
    }
}

// Build one conversation's captured calls newer than `sinceMs`, or null when it
// has none.
//
// One pass accumulates each message's wire form and size, so when the walk
// reaches an assistant message the running length IS that call's
// `request_message_count`. The transcript and its measurements are therefore
// built once for the whole conversation rather than once per call.
export function buildConversationCapture({
    conversation,
    parentToolCallId = null,
    messages,
    systemPrompt,
    sinceMs = 0,
}) {
    if (!Array.isArray(messages)) return null;

    const wire = [];
    const sizes = [];
    const calls = [];
    for (const message of messages) {
        const at = Number(message?.timestamp);
        if (message?.role === "assistant" && Number.isFinite(at) && at > sinceMs) {
            // Everything accumulated so far is exactly what this call sent.
            calls.push({ at, message, count: wire.length });
        }
        const wireMessage = toWireMessage(message);
        if (wireMessage) {
            wire.push(wireMessage);
            sizes.push(messageWireSize(wireMessage));
        }
    }
    if (!calls.length) return null;

    // Only the messages some call actually sent need to travel; a trailing
    // assistant message is in no request but its own, which is by definition
    // not part of what it sent.
    const needed = calls[calls.length - 1].count;
    // Trimming may not run past the shortest request, or that call would
    // describe a conversation it never saw.
    const maxDrop = Math.max(0, calls[0].count - 1);
    const fitted = fitConversation(
        wire.slice(0, needed),
        sizes.slice(0, needed),
        maxDrop,
        MAX_CONVERSATION_BYTES,
    );

    const exchanges = calls.map(({ at, message, count }) => {
        const exchange = {
            exchange_id: String(at),
            at_ms: at,
            request_message_count: Math.max(0, count - fitted.dropped),
        };
        if (message.provider) exchange.provider = String(message.provider);
        const model = message.responseModel || message.model;
        if (model) exchange.model = String(model);
        if (message.responseId) exchange.response_id = String(message.responseId);
        if (message.stopReason) exchange.stop_reason = String(message.stopReason);
        applyUsage(exchange, message.usage);
        // A trim drops context every call in this conversation had, so the flag
        // belongs on all of them.
        if (fitted.truncated) exchange.truncated = true;
        return exchange;
    });

    const record = {
        conversation,
        messages: fitted.messages,
        exchanges,
    };
    if (parentToolCallId) record.parent_tool_call_id = String(parentToolCallId);
    if (systemPrompt) record.system_prompt = String(systemPrompt);
    return record;
}

// The bytes one assembled conversation adds to the body. The messages are now
// structured rather than a pre-stringified field, so this stringifies the whole
// record once - which is exactly the form postChatSync sends.
export function conversationWireBytes(conversation) {
    return wireByteLen(JSON.stringify(conversation));
}

// Assemble every conversation's new calls for one boundary.
//
// `cursors` maps a conversation key to the newest `at_ms` already acked; the
// returned `cursors` advance only over calls this POST actually carries, so a
// conversation held back by the byte or count budget goes out on the next
// boundary instead of being silently lost.
//
// Returns `{ conversations, cursors, bytes, deferred }`. `bytes` is what capture
// adds to the POST — measured here because the budget loop already sizes each
// conversation, so the engine's first-batch reservation doesn't re-serialize
// them. `deferred` is how many exchanges were held back; the server meters it so
// a chat publishing only part of each run is visible instead of looking
// identical to one publishing all of it.
export function buildChatCapture({ agentMessages, systemPrompt, items, cursors = {}, capturedThrough = 0 }) {
    const sources = [
        { conversation: PARENT_CONVERSATION, messages: agentMessages, systemPrompt },
        ...collectSubAgentConversations(items),
    ];

    const candidates = [];
    for (const source of sources) {
        const wasCapturedThisSession = Object.prototype.hasOwnProperty.call(
            cursors,
            source.conversation,
        );
        const sinceMs = Math.max(
            Number(cursors[source.conversation]) || 0,
            Number(capturedThrough) || 0,
        );
        const record = buildConversationCapture({
            conversation: source.conversation,
            // Parentage describes the child launch, not every later boundary.
            // The restore floor is global and does not prove this particular
            // child relationship was captured, so only its own acked cursor
            // suppresses the stable relationship on later boundaries.
            parentToolCallId: wasCapturedThisSession ? null : source.parentToolCallId,
            messages: source.messages,
            systemPrompt: source.systemPrompt,
            // The restore floor and the per-conversation cursor are both
            // "already captured" claims; the stronger one wins.
            sinceMs,
        });
        if (record) candidates.push(record);
    }
    // Oldest activity first, so a budget cut holds back the conversations most
    // likely to still be re-derivable on the next boundary.
    candidates.sort((a, b) => a.exchanges[0].at_ms - b.exchanges[0].at_ms);

    const conversations = [];
    const nextCursors = { ...cursors };
    let bytes = 0;
    let exchangeCount = 0;
    let deferred = 0;
    for (const record of candidates) {
        const size = conversationWireBytes(record);
        const fits =
            exchangeCount + record.exchanges.length <= MAX_EXCHANGES_PER_SYNC
            && (conversations.length === 0 || bytes + size <= MAX_CAPTURE_BYTES);
        if (!fits) {
            // Held whole: splitting a conversation would send its transcript
            // twice, which is the cost this shape exists to avoid.
            deferred += record.exchanges.length;
            continue;
        }
        bytes += size;
        exchangeCount += record.exchanges.length;
        conversations.push(record);
        const newest = record.exchanges[record.exchanges.length - 1].at_ms;
        const prior = Number(nextCursors[record.conversation]) || 0;
        nextCursors[record.conversation] = Math.max(prior, newest);
    }
    return { conversations, cursors: nextCursors, bytes, deferred };
}
